import { HELP_ANCHORS } from '../config/helpAnchors';
import { HELP_GUIDE_SECTIONS } from '../config/helpGuide';

export const ORIENTATION_VERSION = 2;

export const ORIENTATION_TOUR = Object.freeze([
    Object.freeze({
        id: 'workflow-overview',
        contentId: HELP_GUIDE_SECTIONS.WORKFLOW_OVERVIEW,
        title: 'How reports are created',
        description: 'New Report follows three stages: enter and review scan data, choose and configure reports, then generate and track the results.',
        routes: Object.freeze(['/dashboard', '/new-report']),
        preferredRoute: '/dashboard',
        targetCandidates: Object.freeze([]),
        placement: 'center',
        missingTarget: 'center',
    }),
    Object.freeze({
        id: 'start-new-report',
        contentId: HELP_GUIDE_SECTIONS.WORKFLOW_OVERVIEW,
        title: 'Start with New Report',
        description: 'Open New Report from the Dashboard or navigation whenever you are ready to prepare a report. The tutorial will move there before the next step.',
        routes: Object.freeze(['/dashboard', '/new-report']),
        preferredRoute: '/dashboard',
        targetCandidates: Object.freeze([
            HELP_ANCHORS.NEW_REPORT_DASHBOARD,
            HELP_ANCHORS.NEW_REPORT_NAV_DESKTOP,
            HELP_ANCHORS.NEW_REPORT_NAV_MOBILE,
        ]),
        placement: 'right',
        missingTarget: 'center',
    }),
    Object.freeze({
        id: 'prepare-scan-data',
        contentId: HELP_GUIDE_SECTIONS.PREPARE_DATA,
        title: 'Prepare the scan data',
        description: 'Start with the template for the report you intend to create then input the scan data. You can choose to include sample data for reference.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([HELP_ANCHORS.INPUT_TEMPLATES]),
        placement: 'right',
        missingTarget: 'center',
    }),
    Object.freeze({
        id: 'import-or-enter-scans',
        contentId: HELP_GUIDE_SECTIONS.INPUT_SCAN_DATA,
        title: 'Import or enter scans',
        description: 'Import a prepared CSV file through the dropbox or add scans manually below. The tutorial will pause so you can work with the real controls and will continue after a scan appears.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([HELP_ANCHORS.INPUT_UPLOAD]),
        placement: 'left',
        missingTarget: 'center',
        gate: Object.freeze({
            actionLabel: 'Add scans now',
            checkpointId: HELP_ANCHORS.INPUT_REVIEW_READY,
            resumeStepId: 'review-scans-entered',
            resumeMode: 'automatic',
            restoreTargets: Object.freeze([
                Object.freeze({
                    checkpointId: HELP_ANCHORS.INPUT_UPLOAD,
                    stepId: 'import-or-enter-scans',
                }),
            ]),
            waitingTitle: 'Tutorial paused for scan input',
            waitingDescription: 'Import a prepared file or add a blank scan. The tutorial will continue when at least one scan appears in Scans Entered.',
        }),
    }),
    Object.freeze({
        id: 'review-scans-entered',
        contentId: HELP_GUIDE_SECTIONS.INPUT_SCAN_DATA,
        title: 'Review Scans Entered',
        description: 'You can review and edit the scans you entered by expanding the boxes. Verify that the data is correct before continuing. You can also add new scans or remove existing ones.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([
            HELP_ANCHORS.INPUT_REVIEW_READY,
            HELP_ANCHORS.INPUT_REVIEW,
        ]),
        placement: 'left',
        missingTarget: 'center',
        missingTargetStepId: 'import-or-enter-scans',
    }),
    Object.freeze({
        id: 'continue-to-choose-reports',
        contentId: HELP_GUIDE_SECTIONS.INPUT_SCAN_DATA,
        title: 'Continue when the scans are ready',
        description: 'Choose Next Step after reviewing the scans. Normative will keep you here if no scans are present or required data is invalid; correct the highlighted issues and try again.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([HELP_ANCHORS.INPUT_NEXT_STEP]),
        placement: 'left',
        missingTarget: 'center',
        gate: Object.freeze({
            actionLabel: 'Review scans and continue',
            checkpointId: HELP_ANCHORS.REPORT_SELECTION,
            resumeStepId: 'choose-reports',
            resumeMode: 'automatic',
            restoreTargets: Object.freeze([
                Object.freeze({
                    checkpointId: HELP_ANCHORS.INPUT_REVIEW_READY,
                    stepId: 'continue-to-choose-reports',
                }),
                Object.freeze({
                    checkpointId: HELP_ANCHORS.INPUT_UPLOAD,
                    stepId: 'import-or-enter-scans',
                }),
            ]),
            waitingTitle: 'Tutorial paused for scan review',
            waitingDescription: 'Review and correct the scans, then choose Next Step. The tutorial will continue only after Normative successfully opens Choose Reports.',
        }),
    }),
    Object.freeze({
        id: 'choose-reports',
        contentId: HELP_GUIDE_SECTIONS.CHOOSE_REPORTS,
        title: 'Choose the reports to generate',
        description: 'Choose the type of report you want to generate. Report availability is based on the scans you have entered. You can select multiple reports to generate.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([HELP_ANCHORS.REPORT_SELECTION]),
        placement: 'right',
        missingTarget: 'pause',
        missingTargetStepId: 'continue-to-choose-reports',
        gate: Object.freeze({
            actionLabel: 'Choose reports now',
            checkpointId: HELP_ANCHORS.REPORT_QUEUE_READY,
            resumeStepId: 'review-report-queue',
            resumeMode: 'automatic',
            restoreTargets: Object.freeze([
                Object.freeze({
                    checkpointId: HELP_ANCHORS.REPORT_SELECTION,
                    stepId: 'choose-reports',
                }),
                Object.freeze({
                    checkpointId: HELP_ANCHORS.INPUT_REVIEW_READY,
                    stepId: 'continue-to-choose-reports',
                }),
                Object.freeze({
                    checkpointId: HELP_ANCHORS.INPUT_UPLOAD,
                    stepId: 'import-or-enter-scans',
                }),
            ]),
            waitingTitle: 'Tutorial paused for report selection',
            waitingDescription: 'Select at least one available report. The tutorial will continue after Normative finishes preparing a nonempty Report Queue.',
        }),
    }),
    Object.freeze({
        id: 'review-report-queue',
        contentId: HELP_GUIDE_SECTIONS.CUSTOMIZE_REPORTS,
        title: 'Review the Report Queue',
        description: 'Expand the queue to review or edit prepared reports, resolve reports that need attention, and inspect compatibility details. Additionally, you can add a custom report tailored to your needs.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([HELP_ANCHORS.REPORT_QUEUE_READY]),
        placement: 'left',
        missingTarget: 'pause',
        missingTargetStepId: 'choose-reports',
        nextLabel: 'Continue to Generate',
        practice: Object.freeze({
            actionLabel: 'Explore queue controls',
            checkpointId: null,
            resumeStepId: 'generate-and-track',
            resumeMode: 'manual',
            waitingTitle: 'Tutorial paused for queue review',
            waitingDescription: 'Explore the Report Queue, edit or add reports if needed, and review compatibility details. Choose Continue tutorial when you are ready.',
        }),
    }),
    Object.freeze({
        id: 'generate-and-track',
        contentId: HELP_GUIDE_SECTIONS.GENERATE_REPORTS,
        title: 'Generate and track reports',
        description: 'When you are ready, submit the reports for processing. You can track the progress of report generation from the Reports page.',
        routes: Object.freeze(['/new-report']),
        preferredRoute: '/new-report',
        targetCandidates: Object.freeze([HELP_ANCHORS.REPORT_GENERATE]),
        placement: 'left',
        missingTarget: 'pause',
        missingTargetStepId: 'choose-reports',
    }),
]);

export const ORIENTATION_STEP_IDS = Object.freeze(
    ORIENTATION_TOUR.map((step) => step.id)
);

export const getOrientationStepIndex = (stepId) => (
    ORIENTATION_STEP_IDS.includes(stepId)
        ? ORIENTATION_STEP_IDS.indexOf(stepId)
        : 0
);

export const findOrientationInteraction = ({ checkpointId = null, resumeStepId } = {}) => {
    for (const step of ORIENTATION_TOUR) {
        for (const interaction of [step.gate, step.practice]) {
            if (
                interaction
                && interaction.resumeStepId === resumeStepId
                && interaction.checkpointId === checkpointId
            ) return interaction;
        }
    }

    return null;
};
